import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ELoadStatus } from '../../../../../state/state.interface';
import { IBaseResponse } from '../../../../../utilities/http-utilities.service';
import { IDataSource, IDataSourceEditPayload, TDataSourceAddEditPayload } from '../data-sources.interface';

export interface IDataSourcesAddEditDialogState {
  createUpdateStatus: ELoadStatus;
}

@Injectable()
export class DataSourcesAddEditDialogStore extends ComponentStore<IDataSourcesAddEditDialogState> {
  private readonly http = inject(HttpClient);

  public readonly afterCreateUpdateSuccess$ = this.select(({ createUpdateStatus }) => createUpdateStatus).pipe(
    filter((status) => status === ELoadStatus.success),
  );

  readonly createOne = this.effect((trigger$: Observable<TDataSourceAddEditPayload>) =>
    trigger$.pipe(
      switchMap((item) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ createUpdateStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error creating data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IDataSourceEditPayload>) =>
    trigger$.pipe(
      switchMap((item) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ createUpdateStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error updating data', error),
          ),
        );
      }),
    ),
  );

  constructor() {
    super({
      createUpdateStatus: ELoadStatus.initial,
    });
  }

  private createItem(body: TDataSourceAddEditPayload): Observable<IBaseResponse<IDataSource>> {
    return this.http.post<IBaseResponse<IDataSource>>('data-sources', body);
  }

  private updateItem(edit: IDataSourceEditPayload): Observable<IBaseResponse<IDataSource>> {
    return this.http.patch<IBaseResponse<IDataSource>>(`data-sources/${edit.id}`, edit.dto);
  }
}
